import { GridFilterItem, GridSortItem } from '@mui/x-data-grid-pro';

import { httpClient, HttpMethod } from './http-client';
import { AppUser, CustomPermission, LoggedInUser, QueryOption, CSVExportUser } from '../@types';
import { ProfileConfig } from '../pages/Profile/Profile.props';
import { isNotArrayEmpty } from '../utils/array-utils';

// Response type for user list
export interface UserListResponse {
  previousPage?: number;
  currentPage?: number;
  nextPage?: number;
  total?: number;
  limit?: number;
  data?: AppUser[];
  filterResponse?: CSVExportUser[];
}

// Response type for user creation
interface UserCreateResponse {
  id: number;
  [key: string]: unknown;
}

// Response type for user update
interface UserUpdateResponse {
  id: number;
  success?: boolean;
}

// Payload for email verification update
interface EmailVerificationPayload {
  isEmailVerified: boolean;
}

// Auth user data from login provider
interface AuthUserLoginData {
  given_name?: string;
  family_name?: string;
  email?: string;
  sub?: string;
  blocked?: boolean;
  email_verified?: boolean;
  picture?: string;
  updated_at?: string;
}

export const getUsers = (params: QueryOption): Promise<UserListResponse> => {
  const { pageSize, page, filterModel, sortModel } = params;
  let url = `/users?limit=${pageSize}&page=${page + 1}`;

  if (sortModel && isNotArrayEmpty(sortModel as GridSortItem[], 0)) {
    url += `&order_by=${sortModel[0]?.field}&order_direction=${sortModel[0]?.sort}`;
  }

  filterModel.items?.forEach((filter: GridFilterItem) => {
    const field = filter?.field;
    const operator = filter?.operator;
    const value = filter?.value ?? '';
    url += `&${field}=${operator},${value}`;
  });
  return httpClient<UserListResponse>(url, HttpMethod.Get);
};

export const getUserById = (id: number): Promise<AppUser> => {
  return httpClient<AppUser>(`/users/${id}`, HttpMethod.Get);
};

export const getUserByAuth0Id = (id: string): Promise<AppUser> => {
  return httpClient<AppUser>(`/users/byAuth0Id/${id}`, HttpMethod.Get);
};

export const createUser = (user: Partial<AppUser>): Promise<UserCreateResponse> => {
  return httpClient<UserCreateResponse>('/users', HttpMethod.Post, user);
};

export const updateUser = (id: number, user: Partial<AppUser>): Promise<UserUpdateResponse> => {
  return httpClient<UserUpdateResponse>(`/users/${id}`, HttpMethod.Put, user);
};

export const updateUserEmailVerification = (id: number, user: EmailVerificationPayload): Promise<UserUpdateResponse> => {
  return httpClient<UserUpdateResponse>(`/users/${id}/verifyemail`, HttpMethod.Put, user);
};

export const getUserPermissions = (id: number | string): Promise<CustomPermission[]> => {
  return httpClient<CustomPermission[]>(`/users/${id}/permissions`, HttpMethod.Get);
};

export const getUserSystemPermissions = (): Promise<CustomPermission[]> => {
  return httpClient<CustomPermission[]>('/getUserPermissions', HttpMethod.Get);
};

export const createUserPermissions = (id: string, userPermissions: CustomPermission[]): Promise<void> => {
  return httpClient<void>(`/users/${id}/permissions`, HttpMethod.Post, { userPermissions });
};

export const createUserWhenLogin = (authUser: AuthUserLoginData): Promise<UserCreateResponse> => {
  const user = {
    firstName: authUser?.given_name,
    lastName: authUser?.family_name,
    email: authUser?.email,
    contactEmail: authUser?.email,
    auth0Id: authUser?.sub,
    systemRoleId: 101,
    isActive: authUser?.blocked !== true,
    isLocked: false,
    isEmailVerified: authUser?.email_verified,
    structureId: 1,
    profilePic: authUser?.picture,
    timezoneId: 1,
    notes: 'Add note',
    lastActive: authUser?.updated_at,
  };

  return httpClient<UserCreateResponse>('/users/activedirectoryuser', HttpMethod.Post, user);
};

export const getLoggedInUserData = (): Promise<LoggedInUser> => {
  return httpClient<LoggedInUser>('/userLogin', HttpMethod.Get);
};

export const updateProfileConfig = (id: number, config: ProfileConfig): Promise<void> => {
  return httpClient<void>(`/users/profile/${id}`, HttpMethod.Put, config);
};